// Accounts Receivable — suspend / reactivate XSiteIO clients when a bill
// goes unpaid. Admin-only. Suspending a client flips them to inactive in
// XSiteIO, which shows them the "Account Suspended — contact Accounts
// Receivable" page until they're reactivated. Every action is logged.
//
// Dave 2026-10-21.
//
// QuickBooks: "Sync from QuickBooks" copies every open QBO invoice into
// Command; each client row then shows its open balance, overdue amount
// and oldest days past due (CAD; USD invoices converted at QBO's rate).
// Balances reach a row via sites -> customer contact -> qbo_customer_id,
// so customers must be linked by the Customers page QBO sync first.
// Open QBO balances that don't reach any row are listed as unmatched,
// and "Match QuickBooks customers" pairs them with existing Command
// customers (qbo_customer_links; see qboMatchController.js).
// Multi-location customers (one QBO customer, many sites, e.g. Centex):
// invoices are placed on sites from their text (services/invoiceLocator.js);
// anything unplaced is listed under "need a location" for a manual pick.
// The aging figures (Overdue, Current, 1-30 ... 90+) are clickable: each
// lists every QuickBooks customer with money in that range (AgingPanel).

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';

function money(v) {
  if (v === '' || v == null) return '';
  const num = typeof v === 'number' ? v : parseFloat(v);
  if (!Number.isFinite(num)) return '';
  return `$${num.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function daysLabel(d) {
  if (!d) return 'current';
  return `${d} day${d === 1 ? '' : 's'} past due`;
}
function agingColor(days) {
  if (days > 60) return 'var(--color-bad, #b91c1c)';
  if (days > 0) return 'var(--color-warn)';
  return 'var(--color-text-dim)';
}
function capFreq(f) {
  if (!f) return '';
  return f.charAt(0).toUpperCase() + f.slice(1);
}

// ---- QuickBooks customer matching panel ----------------------------
// Pairs each QBO customer that has open invoices with an EXISTING Command
// customer. Suggestions come from name / email / phone similarity; nothing
// is linked until "Save links" is clicked. Never creates or edits contacts.
const AUTO_TICK = 0.7; // suggestions at or above this start ticked

function pct(n) { return `${Math.round(Number(n || 0) * 100)}%`; }

function QboMatchPanel({ onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [tab, setTab] = useState('unlinked');
  const [picks, setPicks] = useState({});     // qid -> { contact_id, label, ticked }
  const [searchFor, setSearchFor] = useState(null); // qid with open search box
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get('/api/ar/qbo-match');
      const list = r.data.rows || [];
      setRows(list);
      setWarning(r.data.warning || null);
      const init = {};
      for (const row of list) {
        if (row.status !== 'unlinked') continue;
        const top = row.candidates?.[0];
        if (top) init[row.qbo_customer_id] = { contact_id: top.contact_id, label: top.label, ticked: top.score >= AUTO_TICK };
      }
      setPicks(init);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadMatches(); }, [loadMatches]);

  useEffect(() => {
    if (!searchFor || searchQ.trim().length < 2) { setSearchRes([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get('/api/ar/qbo-match/contacts', { params: { q: searchQ.trim() } });
        setSearchRes(r.data.contacts || []);
      } catch (e) { setSearchRes([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchFor, searchQ]);

  const unlinked = rows.filter((r) => r.status === 'unlinked');
  const linked = rows.filter((r) => r.status === 'linked');
  const tickedCount = unlinked.filter((r) => picks[r.qbo_customer_id]?.ticked).length;
  const tickedTotal = unlinked.filter((r) => picks[r.qbo_customer_id]?.ticked).reduce((t, r) => t + r.balance, 0);

  function setPick(qid, p) { setPicks((prev) => ({ ...prev, [qid]: { ...prev[qid], ...p } })); }

  function choose(row, value) {
    const qid = row.qbo_customer_id;
    if (value === '__search') { setSearchFor(qid); setSearchQ(''); return; }
    if (value === '') { setPick(qid, { contact_id: null, label: null, ticked: false }); return; }
    const c = row.candidates.find((x) => String(x.contact_id) === value);
    if (c) setPick(qid, { contact_id: c.contact_id, label: c.label, ticked: true });
  }

  async function save() {
    const links = unlinked
      .filter((r) => picks[r.qbo_customer_id]?.ticked && picks[r.qbo_customer_id]?.contact_id)
      .map((r) => ({ qbo_customer_id: r.qbo_customer_id, contact_id: picks[r.qbo_customer_id].contact_id, qbo_name: r.qbo_name }));
    if (links.length === 0) return;
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await axios.post('/api/ar/qbo-match', { links });
      setMsg(`Linked ${r.data.saved} QuickBooks customer${r.data.saved === 1 ? '' : 's'}.`);
      await loadMatches();
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function doUnlink(row) {
    if (!window.confirm(`Unlink ${row.qbo_name} from ${row.contact_label}?`)) return;
    setErr(null);
    setMsg(null);
    try {
      await axios.delete(`/api/ar/qbo-match/${encodeURIComponent(row.qbo_customer_id)}`);
      setMsg(`Unlinked ${row.qbo_name}.`);
      await loadMatches();
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }

  const small = { display: 'block', fontSize: 11, color: 'var(--color-text-dim)' };

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Match QuickBooks customers</h3>
        <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
          Pair each QuickBooks customer with the existing Command customer. Nothing is created or changed in Contacts or QuickBooks.
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-secondary btn-sm" onClick={onClose}>Close</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button type="button" className={tab === 'unlinked' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'} onClick={() => setTab('unlinked')}>
          To match ({unlinked.length})
        </button>
        <button type="button" className={tab === 'linked' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'} onClick={() => setTab('linked')}>
          Linked ({linked.length})
        </button>
      </div>

      {warning && <p style={{ fontSize: 12, color: 'var(--color-warn)' }}>{warning}</p>}
      {err && <p style={{ fontSize: 13, color: 'var(--color-bad)' }}>{err}</p>}
      {msg && <p style={{ fontSize: 13, color: 'var(--color-good, #0d9488)' }}>{msg}</p>}

      {loading ? <p className="cell-muted">Loading suggestions…</p> : tab === 'unlinked' ? (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th aria-label="Link"></th><th>QuickBooks customer</th><th>Open</th><th>Command customer</th></tr></thead>
              <tbody>
                {unlinked.length === 0 ? (
                  <tr><td colSpan={4} className="cell-muted">Every QuickBooks customer with open invoices is linked.</td></tr>
                ) : unlinked.map((row) => {
                  const qid = row.qbo_customer_id;
                  const p = picks[qid] || {};
                  const inCandidates = row.candidates.some((c) => c.contact_id === p.contact_id);
                  return (
                    <tr key={qid}>
                      <td>
                        <input type="checkbox" checked={!!p.ticked} disabled={!p.contact_id}
                          onChange={(e) => setPick(qid, { ticked: e.target.checked })} aria-label={`Link ${row.qbo_name}`} />
                      </td>
                      <td>
                        {row.qbo_name}
                        <span style={small}>
                          {[row.qbo_parent && `sub-customer of ${row.qbo_parent}`, row.qbo_email, row.qbo_phone, row.qbo_city].filter(Boolean).join(' · ')}
                        </span>
                      </td>
                      <td className="cell-mono">
                        {money(row.balance)}
                        <span style={small}>{row.invoice_count} inv</span>
                      </td>
                      <td style={{ minWidth: 300 }}>
                        <select
                          value={p.contact_id ? String(p.contact_id) : ''}
                          onChange={(e) => choose(row, e.target.value)}
                          style={{ width: '100%' }}
                        >
                          <option value="">— No match / skip —</option>
                          {row.candidates.map((c) => (
                            <option key={c.contact_id} value={String(c.contact_id)}>
                              {c.label}{c.city ? ` (${c.city})` : ''} · {pct(c.score)}{c.on_ar_list ? '' : ' · not on A/R list'}
                            </option>
                          ))}
                          {p.contact_id && !inCandidates && <option value={String(p.contact_id)}>{p.label} (picked)</option>}
                          <option value="__search">Search for another customer…</option>
                        </select>
                        {p.contact_id && inCandidates && (
                          <span style={small}>{row.candidates.find((c) => c.contact_id === p.contact_id)?.reasons.join(', ')}</span>
                        )}
                        {searchFor === qid && (
                          <div style={{ marginTop: 6 }}>
                            <input type="text" autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                              placeholder="Company, contact or site name" style={{ width: '100%' }} />
                            {searchRes.length > 0 && (
                              <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                                {searchRes.map((c) => (
                                  <button type="button" key={c.contact_id}
                                    onClick={() => { setPick(qid, { contact_id: c.contact_id, label: c.label, ticked: true }); setSearchFor(null); }}
                                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 0, padding: '6px 8px', cursor: 'pointer', font: 'inherit' }}>
                                    {c.label}
                                    <span style={small}>{[c.city, c.province, `${c.site_count} site${c.site_count === 1 ? '' : 's'}`, c.on_ar_list ? null : 'not on A/R list'].filter(Boolean).join(' · ')}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 4 }} onClick={() => setSearchFor(null)}>Cancel search</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn-primary" onClick={save} disabled={saving || tickedCount === 0}>
              {saving ? 'Saving…' : `Save ${tickedCount} link${tickedCount === 1 ? '' : 's'}`}
            </button>
            <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
              {tickedCount > 0 && `${money(tickedTotal)} open will move onto client rows. `}
              Suggestions of {pct(AUTO_TICK)} or more start ticked; check each one before saving.
            </span>
          </div>
        </>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>QuickBooks customer</th><th>Open</th><th>Command customer</th><th aria-label="Action"></th></tr></thead>
            <tbody>
              {linked.length === 0 ? (
                <tr><td colSpan={4} className="cell-muted">Nothing linked yet.</td></tr>
              ) : linked.map((row) => (
                <tr key={row.qbo_customer_id}>
                  <td>{row.qbo_name}</td>
                  <td className="cell-mono">{money(row.balance)}</td>
                  <td>
                    {row.contact_label}
                    <span style={{ ...small, color: row.on_ar_list ? 'var(--color-text-dim)' : 'var(--color-warn)' }}>
                      {row.link_source === 'customer_sync' ? 'linked by the old customer sync' : `linked ${row.linked_on || ''}`}
                      {row.on_ar_list ? '' : ' · this customer has no XSiteIO client, so the balance stays unmatched on the A/R list'}
                    </span>
                  </td>
                  <td>
                    {row.link_source === 'match' && (
                      <button type="button" className="btn-secondary btn-sm" onClick={() => doUnlink(row)}>Unlink</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Invoices that need a location ---------------------------------
// For customers billed under one QuickBooks customer but running several
// XSiteIO clients (e.g. Centex). Command reads each invoice's memo,
// ship-to and line descriptions to place it on a site; the share it
// can't place is listed here for a manual pick. Picks are kept across
// syncs and cover only the unplaced share.
function NeedsLocationPanel({ items, onClose, onSaved }) {
  const [sitesByCustomer, setSitesByCustomer] = useState({}); // qbo_customer_id -> sites[]
  const [choice, setChoice] = useState({});                   // qbo_invoice_id -> site_id
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const firstInvoiceByCustomer = new Map();
      for (const it of items) if (!firstInvoiceByCustomer.has(it.qbo_customer_id)) firstInvoiceByCustomer.set(it.qbo_customer_id, it.qbo_invoice_id);
      const out = {};
      for (const [qid, invId] of firstInvoiceByCustomer) {
        try {
          const r = await axios.get(`/api/ar/invoice-location/${encodeURIComponent(invId)}/sites`);
          out[qid] = r.data.sites || [];
        } catch (e) { out[qid] = []; }
      }
      if (!cancelled) setSitesByCustomer(out);
    })();
    return () => { cancelled = true; };
  }, [items]);

  async function place(it) {
    const siteId = choice[it.qbo_invoice_id];
    if (!siteId) return;
    setBusy(it.qbo_invoice_id);
    setErr(null);
    setMsg(null);
    try {
      const body = siteId === '__split'
        ? { qbo_invoice_id: it.qbo_invoice_id, split: true }
        : { qbo_invoice_id: it.qbo_invoice_id, site_id: Number(siteId) };
      await axios.post('/api/ar/invoice-location', body);
      setMsg(`Invoice ${it.doc_number || it.qbo_invoice_id} ${siteId === '__split' ? 'split across all locations' : 'placed'}.`);
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  }

  const small = { display: 'block', fontSize: 11, color: 'var(--color-text-dim)' };
  const siteLabel = (s) => [s.site_name || `Site ${s.id}`, s.site_number, s.city].filter(Boolean).join(' · ');

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Invoices that need a location</h3>
        <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
          These customers have several locations under one QuickBooks customer, and the invoice text doesn't say clearly which location this amount is for. Pick the site; it sticks through future syncs.
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-secondary btn-sm" onClick={onClose}>Close</button>
      </div>
      {err && <p style={{ fontSize: 13, color: 'var(--color-bad)' }}>{err}</p>}
      {msg && <p style={{ fontSize: 13, color: 'var(--color-good, #0d9488)' }}>{msg}</p>}
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Customer</th><th>Due</th><th>To place</th><th>What the invoice says</th><th>Site</th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="cell-muted">Every invoice is placed on a location.</td></tr>
            ) : items.map((it) => {
              const sites = sitesByCustomer[it.qbo_customer_id];
              return (
                <tr key={it.qbo_invoice_id}>
                  <td className="cell-mono">{it.doc_number || '—'}</td>
                  <td>{it.customer_name}</td>
                  <td className="cell-mono">
                    {it.due_date}
                    <span style={{ ...small, color: agingColor(it.days_past_due) }}>{daysLabel(it.days_past_due)}</span>
                  </td>
                  <td className="cell-mono">
                    {money(it.unassigned)}
                    {it.unassigned < it.home_balance - 0.01 && <span style={small}>of {money(it.home_balance)}</span>}
                  </td>
                  <td style={{ maxWidth: 320, fontSize: 12 }}>
                    {it.snippet || <span className="cell-muted">no description</span>}
                    {it.matched_site && (
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--color-warn)', marginTop: 2 }}>
                        Names {it.matched_site}, but that site has no XSiteIO client linked in Command (or sits under a different customer). Link the site and it will place itself.
                      </span>
                    )}
                  </td>
                  <td style={{ minWidth: 260 }}>
                    {sites === undefined ? <span className="cell-muted">Loading sites…</span> : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select value={choice[it.qbo_invoice_id] || ''} onChange={(e) => setChoice((p) => ({ ...p, [it.qbo_invoice_id]: e.target.value }))} style={{ flex: 1 }}>
                          <option value="">Pick a site…</option>
                          {sites.length > 1 && <option value="__split">Split evenly across all {sites.length} locations</option>}
                          {sites.map((s) => <option key={s.id} value={String(s.id)}>{siteLabel(s)}</option>)}
                        </select>
                        <button type="button" className="btn-primary btn-sm" disabled={!choice[it.qbo_invoice_id] || busy === it.qbo_invoice_id} onClick={() => place(it)}>
                          {busy === it.qbo_invoice_id ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Site column for an invoice. Matched: Command site name + number, with a
// "by name" marker when only the site name (no number/address) matched so
// those can be checked; hover shows the invoice text it was read from.
// Unmatched: the invoice's own text in grey italics.
function SiteCell({ i }) {
  if (i.site_label) {
    const tip = `Read from invoice text: "${i.location_text || ''}"\nMatched on ${i.site_how}${i.site_words ? `: ${i.site_words}` : ''}`;
    return (
      <span title={tip}>
        {i.site_label}
        {i.site_how === 'name' && (
          <span style={{ fontSize: 11, color: 'var(--color-warn)', marginLeft: 6 }}>by name, check</span>
        )}
      </span>
    );
  }
  if (i.location_text) {
    return (
      <span style={{ fontStyle: 'italic', color: 'var(--color-text-dim)' }} title="From the invoice text; no Command site matched">
        {i.location_text}
      </span>
    );
  }
  return <span className="cell-muted">{'\u2014'}</span>;
}

// ---- Aging drill-down ------------------------------------------------
// Click an aging bucket (or Overdue) in the summary to list every
// QuickBooks customer with money in it, largest first. Built from the
// invoices themselves, so customers not yet matched to an XSiteIO client
// still show. Each customer expands to the invoices in that bucket.
const BUCKET_LABEL = {
  overdue: 'Overdue (1+ days)', current: 'Current (not yet due)', d1_30: '1-30 days past due',
  d31_60: '31-60 days past due', d61_90: '61-90 days past due', d90_plus: 'Over 90 days past due',
};
function inSelection(bucket, sel) {
  return sel === 'overdue' ? bucket !== 'current' : bucket === sel;
}
function csvCell(v) {
  const t = v == null ? '' : String(v);
  return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}

function AgingPanel({ customers, selected, onClose, onFindClient }) {
  const [open, setOpen] = useState(null);

  const list = useMemo(() => (customers || [])
    .map((c) => {
      const invs = c.invoices.filter((i) => inSelection(i.bucket, selected));
      const amount = Math.round(invs.reduce((t, i) => t + i.home_balance, 0) * 100) / 100;
      const oldest = invs.reduce((m, i) => Math.max(m, i.days_past_due), 0);
      return { ...c, invs, amount, oldest };
    })
    .filter((c) => c.amount > 0)
    .sort((a, b) => b.amount - a.amount), [customers, selected]);
  const total = list.reduce((t, c) => t + c.amount, 0);

  function exportCsv() {
    const head = ['Customer', `Amount (${BUCKET_LABEL[selected]})`, 'Total owed', 'Oldest days past due', 'Invoices', 'XSiteIO client(s)'];
    const lines = [head.map(csvCell).join(',')];
    for (const c of list) {
      lines.push([
        c.name, c.amount.toFixed(2), c.total.toFixed(2), c.oldest,
        c.invs.map((i) => `${i.doc_number || '?'} ${i.site_label || i.location_text || ''} (due ${i.due_date || ''}, ${i.home_balance.toFixed(2)})`).join('; '),
        (c.clients || []).map((x) => x.name).join('; '),
      ].map(csvCell).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ar-aging-${selected}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const small = { display: 'block', fontSize: 11, color: 'var(--color-text-dim)' };

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>{BUCKET_LABEL[selected]}</h3>
        <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>
          {list.length} customer{list.length === 1 ? '' : 's'} · {money(total)}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-secondary btn-sm" onClick={exportCsv} disabled={list.length === 0}>Export CSV</button>
        <button type="button" className="btn-secondary btn-sm" onClick={onClose}>Close</button>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Customer (QuickBooks)</th><th>Amount</th><th>Oldest</th><th>Total owed</th><th>XSiteIO client</th></tr></thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={5} className="cell-muted">Nobody owes money in this range.</td></tr>
            ) : list.map((c) => (
              <Fragment key={c.qbo_customer_id}>
                <tr>
                  <td>
                    <button type="button" onClick={() => setOpen(open === c.qbo_customer_id ? null : c.qbo_customer_id)}
                      style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left' }}>
                      {c.name || `QBO customer ${c.qbo_customer_id}`} {open === c.qbo_customer_id ? '▴' : '▾'}
                    </button>
                    <span style={small}>{c.invs.length} invoice{c.invs.length === 1 ? '' : 's'}</span>
                  </td>
                  <td className="cell-mono" style={{ fontWeight: 600 }}>{money(c.amount)}</td>
                  <td style={{ color: agingColor(c.oldest) }}>{c.oldest ? `${c.oldest} days` : 'current'}</td>
                  <td className="cell-mono">{money(c.total)}</td>
                  <td>
                    {(c.clients || []).length === 0 ? (
                      <span style={{ fontSize: 12, color: 'var(--color-warn)' }}>not matched</span>
                    ) : (c.clients.length > 3 ? (
                      <span style={{ fontSize: 12 }}>{c.clients.length} locations</span>
                    ) : c.clients.map((cl) => (
                      <span key={cl.id} style={{ display: 'block', fontSize: 12 }}>
                        <button type="button" onClick={() => onFindClient(cl.name)} title="Show this client in the list below"
                          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', textDecoration: 'underline', color: 'inherit' }}>
                          {cl.name}
                        </button>
                        {cl.suspended && <span style={{ color: 'var(--color-bad, #b91c1c)', fontWeight: 700 }}> · SUSPENDED</span>}
                      </span>
                    )))}
                  </td>
                </tr>
                {open === c.qbo_customer_id && (
                  <tr>
                    <td colSpan={5} style={{ background: 'var(--color-surface-sunken)' }}>
                      <table className="data-table" style={{ margin: 0 }}>
                        <thead><tr><th>Invoice #</th><th>Site</th><th>Due</th><th>Age</th><th>Open</th><th>CAD</th></tr></thead>
                        <tbody>
                          {c.invs.map((i, idx) => (
                            <tr key={`${i.doc_number}-${idx}`}>
                              <td className="cell-mono">{i.doc_number || '—'}</td>
                              <td><SiteCell i={i} /></td>
                              <td className="cell-mono">{i.due_date || ''}</td>
                              <td style={{ color: agingColor(i.days_past_due) }}>{daysLabel(i.days_past_due)}</td>
                              <td className="cell-mono">{money(i.balance)}{i.currency !== 'CAD' ? ` ${i.currency}` : ''}</td>
                              <td className="cell-mono">{money(i.home_balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AccountsReceivable() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [clients, setClients] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [filter, setFilter] = useState('');
  const [show, setShow] = useState('all'); // all | active | suspended | owing | overdue
  const [sort, setSort] = useState('name'); // name | balance | oldest
  const [busyId, setBusyId] = useState(null);
  const [qbo, setQbo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [showNeedsLoc, setShowNeedsLoc] = useState(false);
  const [agingSel, setAgingSel] = useState(null); // null | 'overdue' | bucket key
  const [reconnect, setReconnect] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get('/api/ar/clients');
      setClients(r.data.clients || []);
      setQbo(r.data.qbo || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  const loadLog = useCallback(async () => {
    try {
      const r = await axios.get('/api/ar/log');
      setLogRows(r.data.log || []);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { if (isAdmin) { load(); loadLog(); } }, [isAdmin, load, loadLog]);

  async function syncQbo() {
    setSyncing(true);
    setError(null);
    setInfo(null);
    try {
      setReconnect(false);
      const r = await axios.post('/api/ar/sync-qbo');
      setInfo(`Synced ${r.data.invoices} open invoice${r.data.invoices === 1 ? '' : 's'} from QuickBooks (${money(r.data.open_total)} open).`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setReconnect(!!e.response?.data?.reconnect);
    } finally {
      setSyncing(false);
    }
  }

  async function toggle(c) {
    const suspend = !c.suspended;
    const owing = c.qbo
      ? `\n\nQuickBooks: ${money(c.qbo.balance)} open, ${money(c.qbo.overdue)} overdue (oldest ${daysLabel(c.qbo.oldestDays)}).`
      : '';
    const who = c.display_name && c.display_name !== c.name ? `${c.display_name} (${c.name})` : c.name;
    const msg = suspend
      ? `Suspend ${who}?${owing}\n\nThey will see the "Account Suspended — contact Accounts Receivable" page in XSiteIO until you reactivate them.`
      : `Reactivate ${who}?\n\nThis restores their XSiteIO access.`;
    if (!window.confirm(msg)) return;
    setBusyId(c.id);
    setError(null);
    setInfo(null);
    try {
      const url = `/api/ar/clients/${c.id}/${suspend ? 'suspend' : 'reactivate'}`;
      await axios.post(url, { name: c.name, external_id: c.external_id });
      setInfo(`${who} ${suspend ? 'suspended' : 'reactivated'}.`);
      await Promise.all([load(), loadLog()]);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusyId(null);
    }
  }

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = clients.filter((c) => {
      if (show === 'active' && c.suspended) return false;
      if (show === 'suspended' && !c.suspended) return false;
      if (show === 'owing' && !(c.qbo?.balance > 0)) return false;
      if (show === 'overdue' && !(c.qbo?.overdue > 0)) return false;
      if (!q) return true;
      return [c.display_name, c.name, c.external_id, c.qbo_name, c.command_name]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
    if (sort === 'balance') list.sort((a, b) => (b.qbo?.balance || 0) - (a.qbo?.balance || 0));
    else if (sort === 'oldest') list.sort((a, b) => (b.qbo?.oldestDays || 0) - (a.qbo?.oldestDays || 0) || (b.qbo?.overdue || 0) - (a.qbo?.overdue || 0));
    return list;
  }, [clients, filter, show, sort]);

  const suspendedCount = useMemo(() => clients.filter((c) => c.suspended).length, [clients]);

  if (!isAdmin) {
    return (
      <section className="page">
        <div className="page-header"><h2>Accounts Receivable</h2></div>
        <p className="placeholder-note">This section is admin-only.</p>
      </section>
    );
  }

  return (
    <section className="page">
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <div>
          <h2 style={{ margin: 0 }}>Accounts Receivable</h2>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
            Open balances from QuickBooks; suspend a client in XSiteIO when a bill goes unpaid. They'll see the Account Suspended page until reactivated.
            {clients.length > 0 && <> · {clients.length} clients, {suspendedCount} suspended.</>}
          </div>
        </div>
      </div>

      {qbo?.synced ? (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>Open in QuickBooks</div>
              <div style={{ fontSize: 20, fontWeight: 700 }} className="cell-mono">{money(qbo.open_total)}</div>
            </div>
            {[{ key: 'overdue', label: 'Overdue', amount: qbo.overdue_total, big: true }, ...(qbo.aging || [])].map((b) => {
              const on = agingSel === b.key;
              return (
                <button key={b.key} type="button"
                  onClick={() => setAgingSel(on ? null : b.key)}
                  title={`Show who owes money ${b.key === 'overdue' ? 'past due' : `in ${b.label}`}`}
                  style={{
                    textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
                    background: on ? 'var(--color-surface-sunken)' : 'none',
                    border: `1px solid ${on ? 'var(--color-border)' : 'transparent'}`,
                    borderRadius: 6, padding: '4px 8px', margin: '-4px -8px',
                  }}>
                  <div style={{ fontSize: 11, color: 'var(--color-text-dim)', textDecoration: 'underline dotted' }}>{b.label}</div>
                  <div className="cell-mono" style={b.big
                    ? { fontSize: 20, fontWeight: 700, color: b.amount > 0 ? 'var(--color-bad, #b91c1c)' : undefined }
                    : { fontSize: 14, color: b.key === 'd90_plus' && b.amount > 0 ? 'var(--color-bad, #b91c1c)' : undefined }}>
                    {money(b.amount)}
                  </div>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 8 }}>
            {qbo.invoice_count} open invoice{qbo.invoice_count === 1 ? '' : 's'} · last synced {qbo.last_synced} · amounts in CAD
            {qbo.unmatched_count > 0 && (
              <>
                {' · '}
                <button type="button" className="btn-link" style={{ background: 'none', border: 0, padding: 0, color: 'var(--color-warn)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
                  onClick={() => setShowUnmatched((v) => !v)}>
                  {qbo.unmatched_count} QBO customer{qbo.unmatched_count === 1 ? '' : 's'} ({money(qbo.unmatched_total)}) not matched to a client
                </button>
                {' '}
                <button type="button" className="btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={() => setShowMatch(true)}>
                  Match QuickBooks customers
                </button>
              </>
            )}
            {qbo.unmatched_count === 0 && (
              <>
                {' · '}
                <button type="button" className="btn-link" style={{ background: 'none', border: 0, padding: 0, color: 'inherit', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
                  onClick={() => setShowMatch(true)}>
                  Review QuickBooks links
                </button>
              </>
            )}
            {qbo.needs_location_count > 0 && (
              <>
                {' · '}
                <button type="button" className="btn-link" style={{ background: 'none', border: 0, padding: 0, color: 'var(--color-warn)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
                  onClick={() => setShowNeedsLoc((v) => !v)}>
                  {qbo.needs_location_count} invoice{qbo.needs_location_count === 1 ? '' : 's'} ({money(qbo.needs_location_total)}) need a location
                </button>
              </>
            )}
          </div>
          {showUnmatched && qbo.unmatched?.length > 0 && (
            <div className="table-wrap" style={{ marginTop: 8 }}>
              <table className="data-table">
                <thead><tr><th>QBO customer</th><th>QBO ID</th><th>Invoices</th><th>Open</th></tr></thead>
                <tbody>
                  {qbo.unmatched.map((u) => (
                    <tr key={u.qbo_customer_id}>
                      <td>{u.name || ''}</td>
                      <td className="cell-mono">{u.qbo_customer_id}</td>
                      <td>{u.count}</td>
                      <td className="cell-mono">{money(u.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 4 }}>
                These have open invoices in QuickBooks but don't reach an XSiteIO client yet. Use "Match QuickBooks customers" to link each one to its Command customer. A linked customer still needs sites with an XSiteIO client to appear on a row.
              </div>
            </div>
          )}
        </div>
      ) : qbo?.error ? (
        <p style={{ fontSize: 12, color: 'var(--color-bad)' }}>QuickBooks balances unavailable: {qbo.error}</p>
      ) : (
        <p style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>No QuickBooks data yet. Click "Sync from QuickBooks" to pull open invoices.</p>
      )}

      {agingSel && qbo?.synced && (
        <AgingPanel
          customers={qbo.customers || []}
          selected={agingSel}
          onClose={() => setAgingSel(null)}
          onFindClient={(name) => { setFilter(name); setShow('all'); }}
        />
      )}
      {showMatch && <QboMatchPanel onClose={() => setShowMatch(false)} onSaved={load} />}
      {showNeedsLoc && qbo?.synced && (
        <NeedsLocationPanel items={qbo.needs_location || []} onClose={() => setShowNeedsLoc(false)} onSaved={load} />
      )}

      {error && (
        <p className="error" style={{ color: 'var(--color-bad)' }}>
          {error}
          {reconnect && (
            <>
              {' '}
              <a href="/api/qbo/connect" style={{ fontWeight: 600 }}>Reconnect QuickBooks</a>
            </>
          )}
        </p>
      )}
      {info && <p style={{ color: 'var(--color-good, #0d9488)', fontSize: 13 }}>{info}</p>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <label className="form-field" style={{ flex: '1 1 240px' }}>
          <span>Search</span>
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="QuickBooks or client name, or external ID" />
        </label>
        <label className="form-field">
          <span>Show</span>
          <select value={show} onChange={(e) => setShow(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="suspended">Suspended only</option>
            <option value="owing">Owing in QBO</option>
            <option value="overdue">Overdue in QBO</option>
          </select>
        </label>
        <label className="form-field">
          <span>Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="name">Name</option>
            <option value="balance">Balance (high first)</option>
            <option value="oldest">Oldest overdue</option>
          </select>
        </label>
        <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <button type="button" className="btn-primary" onClick={syncQbo} disabled={syncing || loading}>
          {syncing ? 'Syncing…' : 'Sync from QuickBooks'}
        </button>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>External ID</th><th>Client (QuickBooks)</th><th>QBO Balance</th><th>Next Invoice</th><th>Status</th><th aria-label="Action"></th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="cell-muted">No clients match.</td></tr>
            ) : (
              rows.map((c) => (
                <Fragment key={c.id}>
                <tr>
                  <td className="cell-mono">{c.external_id}</td>
                  <td title={c.name_from_memo ? 'First line of "Memo on statement" on this site\'s latest QuickBooks invoice' : c.qbo_name ? 'QuickBooks customer name' : 'Command customer name (not linked to QuickBooks yet)'}>
                    {c.display_name || c.name}
                    {(c.display_name || c.name) !== c.name && (
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-dim)' }} title="Name in XSiteIO">{c.name}</span>
                    )}
                  </td>
                  <td className="cell-mono">
                    {c.qbo ? (
                      <>
                        <button type="button" onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                          title="Show open invoices"
                          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'inherit' }}>
                          {money(c.qbo.balance)} {expanded === c.id ? '\u25b4' : '\u25be'}
                        </button>
                        <span style={{ display: 'block', fontSize: 11, color: agingColor(c.qbo.oldestDays) }}>
                          {c.qbo.overdue > 0 ? `${money(c.qbo.overdue)} overdue \u00b7 oldest ${c.qbo.oldestDays}d` : 'Not overdue'}
                          {` \u00b7 ${c.qbo.invoiceCount} inv`}
                          {c.qbo.currencies.some((x) => x !== 'CAD') ? ` \u00b7 ${c.qbo.currencies.join('/')}` : ''}
                          {c.qbo.shared ? ' \u00b7 shared' : ''}
                        </span>
                      </>
                    ) : <span className="cell-muted">{qbo?.synced ? '$0.00' : '\u2014'}</span>}
                  </td>
                  <td className="cell-mono">
                    {c.next_invoice ? (
                      <>
                        {money(c.next_invoice.subtotal)}
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-dim)' }}>
                          {c.next_invoice.mixedFreq ? 'Mixed' : capFreq(c.next_invoice.freq)}
                          {c.next_invoice.dueDate ? ` \u00b7 due ${c.next_invoice.dueDate}` : ' \u00b7 no renewal date'}
                          {c.next_invoice.taxIncl ? ` \u00b7 incl. tax ${money(c.next_invoice.taxIncl)}` : ''}
                          {c.next_invoice.siteCount > 1 ? ` \u00b7 ${c.next_invoice.siteCount} sites` : ''}
                        </span>
                      </>
                    ) : <span className="cell-muted">—</span>}
                  </td>
                  <td>
                    {c.suspended
                      ? <span style={{ fontWeight: 700, color: 'var(--color-bad, #b91c1c)' }}>SUSPENDED</span>
                      : <span style={{ color: 'var(--color-good, #0d9488)' }}>Active</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={c.suspended ? 'btn-secondary btn-sm' : 'btn-secondary btn-sm'}
                      style={c.suspended ? { color: 'var(--color-good, #0d9488)' } : { color: 'var(--color-bad, #b91c1c)' }}
                      onClick={() => toggle(c)}
                      disabled={busyId === c.id}
                    >
                      {busyId === c.id ? 'Working…' : (c.suspended ? 'Reactivate' : 'Suspend')}
                    </button>
                  </td>
                </tr>
                {expanded === c.id && c.qbo && (
                  <tr>
                    <td></td>
                    <td colSpan={5} style={{ background: 'var(--color-surface-sunken)' }}>
                      {c.qbo.shared && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginBottom: 4 }}>
                          Shared: this QBO customer feeds more than one XSiteIO client, so these invoices also show on those rows.
                        </div>
                      )}
                      <table className="data-table" style={{ margin: 0 }}>
                        <thead><tr><th>Invoice #</th><th>Site</th><th>Due</th><th>Age</th><th>Open</th><th>CAD</th></tr></thead>
                        <tbody>
                          {c.qbo.invoices.map((i, idx) => (
                            <tr key={`${i.doc_number}-${idx}`}>
                              <td className="cell-mono">{i.doc_number || '\u2014'}</td>
                              <td><SiteCell i={i} /></td>
                              <td className="cell-mono">{i.due_date || ''}</td>
                              <td style={{ color: agingColor(i.days_past_due) }}>{daysLabel(i.days_past_due)}</td>
                              <td className="cell-mono">{money(i.balance)}{i.currency !== 'CAD' ? ` ${i.currency}` : ''}</td>
                              <td className="cell-mono">
                                {money(i.home_balance)}
                                {(i.split || (i.how && i.how !== 'auto')) && (
                                  <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-dim)' }}>
                                    {i.split ? `this site's share of ${money(i.full_home_balance)}` : ''}
                                    {i.split && i.how && i.how !== 'auto' ? ' \u00b7 ' : ''}
                                    {i.how === 'manual' ? 'placed by hand' : i.how === 'split' ? 'split evenly across locations' : i.how === 'mixed' ? 'partly placed by hand' : ''}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'var(--space-5)', marginBottom: 'var(--space-2)' }}>
        <h3 style={{ margin: 0 }}>Recent actions</h3>
        <button type="button" className="btn-secondary btn-sm" onClick={loadLog}>Refresh</button>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>When</th><th>Client</th><th>Action</th><th>By</th><th>Note</th></tr>
          </thead>
          <tbody>
            {logRows.length === 0 ? (
              <tr><td colSpan={5} className="cell-muted">No actions logged yet.</td></tr>
            ) : (
              logRows.map((l) => (
                <tr key={l.id}>
                  <td className="cell-mono">{l.at}</td>
                  <td>{l.client_name || l.client_external_id || l.xsiteio_client_id}</td>
                  <td>{l.action === 'suspend'
                    ? <span style={{ color: 'var(--color-bad, #b91c1c)', fontWeight: 600 }}>Suspended</span>
                    : <span style={{ color: 'var(--color-good, #0d9488)', fontWeight: 600 }}>Reactivated</span>}</td>
                  <td>{l.by_name || ''}</td>
                  <td>{l.note || ''}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
